// Install / update / enable / disable / uninstall orchestration for the
// marketplace. It drives the SAME PluginSystem the app uses: install runs the
// real integrity gate (PluginHost.verifyForInstall = kill switch + verifier)
// BEFORE registering anything, so a revoked or unsigned plugin never installs;
// update diffs new mandatory permissions and, when any appear, leaves the plugin
// DISABLED pending re-consent (spec 9.4). The marketplace never grants silently.

import type { PermissionRequest } from '@slate/plugin-api';
import type { PluginSystem } from '../plugins/setup';
import { DEFAULT_BUILTIN_PLUGIN_IDS } from '../plugins/appPluginSystem';
import {
  localStoragePluginStateStore,
  type PluginStateStore,
} from '../plugins/localStoragePluginStateStore';
import type { PluginSource } from './source';
import { newMandatoryPermissions } from './permissionDisclosure';

export interface InstallFlowDeps {
  system: PluginSystem;
  source: PluginSource;
  /** Persisted lifecycle delta sink. Defaults to localStorage. */
  store?: PluginStateStore;
}

export interface UpdateResult {
  id: string;
  updated: boolean;
  newMandatoryPermissions: PermissionRequest[];
  enabled: boolean;
}

export interface InstallFlow {
  install(id: string): Promise<void>;
  update(id: string): Promise<UpdateResult>;
  setEnabled(id: string, enabled: boolean): Promise<void>;
  uninstall(id: string): Promise<void>;
}

export function createInstallFlow(deps: InstallFlowDeps): InstallFlow {
  const { system, source } = deps;
  const store = deps.store ?? localStoragePluginStateStore;

  // Persist the lifecycle delta DERIVED from live host state, so it stays correct
  // regardless of which path mutated it (this also captures update()'s internal
  // setEnabled(id, false) when a new mandatory permission appears).
  const persist = (): void => {
    const installed = system.host.list().map((p) => p.manifest.id);
    store.set({
      disabled: installed.filter((id) => !system.host.isEnabled(id)),
      uninstalledBuiltins: DEFAULT_BUILTIN_PLUGIN_IDS.filter((id) => !installed.includes(id)),
      installedExtras: installed.filter((id) => !DEFAULT_BUILTIN_PLUGIN_IDS.includes(id)),
    });
  };

  return {
    async install(id) {
      const detail = await source.getDetail(id);
      // Integrity gate FIRST: a revoked id or an unsigned sealed payload throws
      // PluginRefusedError here, before any contribution is registered.
      await system.host.verifyForInstall(detail.install);
      system.install(detail.install); // registers contributions + declares perms
      persist();
    },

    async update(id) {
      const detail = await source.getDetail(id);
      const next = detail.install;
      const prev = system.host.list().find((p) => p.manifest.id === id)?.manifest;
      const added = newMandatoryPermissions(
        prev?.permissions ?? [],
        next.manifest.permissions ?? [],
      );
      await system.host.verifyForInstall(next);
      await system.host.deactivate(id); // tear down the old worker before the swap
      system.install(next); // re-register + re-declare with the new manifest
      if (added.length > 0) {
        await system.host.setEnabled(id, false); // inactive until the user re-consents
      }
      persist();
      return { id, updated: true, newMandatoryPermissions: added, enabled: added.length === 0 };
    },

    async setEnabled(id, enabled) {
      await system.host.setEnabled(id, enabled);
      persist();
    },

    async uninstall(id) {
      await system.host.uninstall(id);
      persist();
    },
  };
}
